import { Button, Input, List, Popconfirm, Popover, Space, Tag, Tooltip } from 'antd';
import Typography from 'antd/es/typography/Typography';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { FaExchangeAlt, FaPencilAlt } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { GoTrash } from 'react-icons/go';
import { ImCheckmark } from 'react-icons/im';
import { useParams } from 'react-router-dom';
import { PriorityOptions, TASk_TABS } from '../../constants';
import { isAdmin, isManager, isStaff, isSuperAdmin, priorityWithIcon } from '../../helper';
import useAuth from '../../hooks/useAuth';
import useLabels from '../../hooks/useLabels';
import useRestaurants from '../../hooks/useRestaurants';
import useTasks from '../../hooks/useTasks';
import { deleteTask, getTasks } from '../../redux/actions/tasks';
import { getHighlightedText } from '../../utils';
import ChecklistProgress from '../ChecklistProgress';
import CompleteTaskModal from '../CompleteTaskModal';
import CreateTask from '../CreateTask';
import DraggableTable from '../DraggableTable';
import DueDateBadge from '../DueDateBadge';
import DueDateFilter from '../DueDateFilter';
import TaskDetailView from '../TaskDetailView';
import CopyDuplicateTaskModal from '../CopyDuplicateTaskModal';
import UserAvatar from '../UserAvatar';
import UserGroup from '../UserGroup';
import { decodeId } from '../../helper/hashids';

const CurrentTasks = ({ restaurant }) => {
  const { id: siteId } = useParams();
  const id = decodeId(siteId);
  const { user } = useAuth();
  const {
    pending: { tasks, pagination },
  } = useTasks();
  const { users, restaurants } = useRestaurants();
  const { labels: allLabels } = useLabels();


  // const [data, setData] = useState(taskList.filter((el) => el.progress !== 'completed') ?? []);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [taskData, setTaskData] = useState();
  const [isTaskEdit, setIsTaskEdit] = useState(false);
  const [isTaskView, setIsTaskView] = useState(false);
  const [IsCopyDuplicateModalOpen, setIsCopyDuplicateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const showMoveDuplicate = isSuperAdmin() || isAdmin() || isManager()
    ? restaurants?.length > 1
    : restaurants?.filter((r) => r.allow_staff_create_task === 1)?.length > 1 && restaurants?.length > 1;

  const handleCompleteClick = (task) => {
    setCurrentTask(task);
    setIsModalVisible(true);
  };

  // const handleOrderChange = (order) => {
  //   localStorage.setItem('currentTaskOrder', JSON.stringify(order));
  //   const currentTaskOrder = updateOrderById(data, order);
  //   setData(currentTaskOrder);
  // };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setCurrentPage(pagination.current);

    const { due_date, ...restFilters } = filters;
    const dateRange = due_date?.[0];
    const start_date = dateRange?.[0] ? dayjs(dateRange[0], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;
    const end_date = dateRange?.[1] ? dayjs(dateRange[1], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;

    getTasks({
      id,
      filter: TASk_TABS.PENDING,
      page: pagination.current,
      ...restFilters,
      start_date,
      end_date,
    });
  };

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size={3} onClick={(e) => e.stopPropagation()}>
          {(isSuperAdmin() || isAdmin() || isManager() || (isStaff() && !!restaurant?.allow_staff_create_task && record?.addedBy === user?.id)) && (
            <Fragment>
              <Tooltip title="Edit">
                <Button
                  onClick={() => {
                    setTaskData(record);
                    setIsTaskEdit(true);
                  }}
                  variant="dashed"
                  color="primary"
                  icon={<FaPencilAlt />}
                  ghost
                  styles={{
                    icon: {
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  size="small"
                />
              </Tooltip>
              <Popconfirm
                title="Are you sure delete this task?"
                okText="Yes"
                okType="danger"
                onConfirm={async () => {
                  const res = await deleteTask(record?.id);
                  if (res.ack) getTasks({ id, filter: TASk_TABS.PENDING, page: currentPage });
                }}
              >
                <Tooltip title="Delete">
                  <Button
                    type="dashed"
                    icon={<GoTrash />}
                    ghost
                    danger
                    styles={{
                      icon: {
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
              {showMoveDuplicate && (
                <Tooltip title="Copy / Move">
                  <Button
                    onClick={() => {
                      setTaskData(record);
                      setIsCopyDuplicateModalOpen(true);
                    }}
                    variant="dashed"
                    color="primary"
                    icon={<FaExchangeAlt />}
                    ghost
                    styles={{
                      icon: {
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                    size="small"
                  />
                </Tooltip>
              )}
            </Fragment>
          )}
          <Tooltip title="Mark as Complete">
            <Button
              onClick={() => handleCompleteClick(record)}
              variant="dashed"
              color="primary"
              disabled={record.progress === 'completed'}
              icon={<ImCheckmark />}
              ghost
              styles={{
                icon: {
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              size="small"
              className="complete-btn"
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filteredValue: filteredInfo.title || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="task-header-search">
          <Input
            placeholder="Search title..."
            size="large"
            allowClear
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value?.trimStart()])}
            onPressEnter={() => confirm()}
            suffix={<FiSearch />}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<FiSearch />} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              color="danger"
              variant="filled"
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <FiSearch style={{ color: filtered ? '#1677ff' : undefined }} />,
      render: (title, record) => {
        const searchText = filteredInfo?.title?.[0]?.toLowerCase();
        return (
          <Fragment>
            <Typography.Text strong ellipsis={{ tooltip: true }}>
              {getHighlightedText(title, searchText)}
            </Typography.Text>
          </Fragment>
        );
      },
    },
    {
      title: 'Assignee',
      dataIndex: 'assignees',
      key: 'assignees',
      width: 120,
      filteredValue: filteredInfo.assignees || null,
      filters: users?.map((user) => {
        return {
          text: (
            <Space align="center">
              <UserAvatar user={user} size="small" style={{ marginRight: 8 }} />
              {`${user.first_name} ${user.last_name ?? ''}`}
            </Space>
          ),
          value: user.id,
        };
      }),
      render: (assignees) => {
        return (
          <UserGroup users={assignees} maxCount={2} />
        );
      },
    },
    {
      title: 'Checklist',
      dataIndex: 'checklist',
      key: 'checklist',
      width: 120,
      render: (checklist) => {
        return <ChecklistProgress trackCheckList={checklist} />;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      filteredValue: filteredInfo.priority || null,
      filters: PriorityOptions.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      render: (priority) => {
        return priorityWithIcon(priority);
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 220,
      filteredValue: filteredInfo.due_date || null,
      filterDropdown: (props) => <DueDateFilter {...props} />,
      render: (date) => {
        return <DueDateBadge date={date} />;
      },
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      width: 150,
      filteredValue: filteredInfo.labels || null,
      filters: allLabels?.map((label) => ({
        text: <Tag color={label.color}>{label.title}</Tag>,
        value: label.id,
      })),
      render: (labels) => {
        const visibleLabel = labels && labels[0];
        const otherLabels = labels?.slice(1);

        return (
          <>
            {visibleLabel && <Tag color={visibleLabel?.color}>{visibleLabel?.title}</Tag>}
            {!!otherLabels?.length && (
              <Popover
                title="Other Labels"
                trigger="hover"
                style={{ padding: 0 }}
                content={
                  <List
                    size="small"
                    dataSource={otherLabels}
                    renderItem={(label) => (
                      <List.Item style={{ padding: '4px 0' }} key={label?.label_id}>
                        <Tag style={{ width: '100%' }} color={label?.color}>
                          {label?.title}
                        </Tag>
                      </List.Item>
                    )}
                  />
                }
              >
                <Tag color="blue">+{otherLabels.length}</Tag>
              </Popover>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    // const order = localStorage.getItem('currentTaskOrder');
    // if (order) {
    //   const currentTaskOrder = updateOrderById(data, JSON.parse(order));
    //   setData(currentTaskOrder);
    // }
    getTasks({ id, filter: TASk_TABS.PENDING, page: currentPage });
  }, []);

  return (
    <div className="CurrentTasks">
      <DraggableTable
        columns={columns}
        data={tasks?.map((task) => ({ ...task, key: task.id }))}
        onOrderChange={() => { }}
        onChange={handleTableChange}
        pagination={{
          total: pagination?.total,
          pageSize: pagination?.limit,
          current: pagination?.page,
          showSizeChanger: false,
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
        }}
        onRow={(record) => ({
          onClick: () => {
            setTaskData(record);
            setIsTaskView(true);
          },
        })}
      />
      {isModalVisible && (
        <CompleteTaskModal
          open={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          task={currentTask}
        />
      )}
      {isTaskEdit && (
        <CreateTask open={isTaskEdit} taskData={taskData} onClose={() => setIsTaskEdit(false)} />
      )}
      {isTaskView && (
        <TaskDetailView open={isTaskView} task={taskData} restaurant={restaurant} onClose={() => setIsTaskView(false)} />
      )}
      {IsCopyDuplicateModalOpen && (
        <CopyDuplicateTaskModal
          open={IsCopyDuplicateModalOpen}
          task={taskData}
          currentRestaurantId={id}
          onClose={() => setIsCopyDuplicateModalOpen(false)}
        />
      )}
    </div>
  );
};

export default CurrentTasks;
